import { ImportService } from '@amzn/awscat-portfolio-assessment-service-client';
import { FileRecord, PropertyConfig, Sheet } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box } from '@amzn/awsui-components-react';
import React, { Component } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';

import { constants } from '../../../common/Localization';
import { ErrorWithResponse } from '../../../common/types';
import GuidedTourContext  from '../../../contexts/GuidedTourContext';
import { RESOURCE_TYPES_MAP, STAGE_VALUES, WIZARD_STEP_VALUES, WIZARD_STEPS_MAP } from './common-util/resources';
import { getNextStep, getPrevStep } from './common-util/stepNavigation';
import { DuplicateRecordsMap, StepNagivationOptions, WizardStepsMap } from './common-util/types';
import { getKey } from './common-util/util';
import { withWizardContext, WizardContextProps } from './contexts/WizardContext';
import WizardActionButtons from './WizardActionButtons';
import WizardContent from './WizardContent';
import WizardNavbarView from './WizardNavbarView';

interface State {
    currentStep: WIZARD_STEP_VALUES;
    duplicateRecordsMap: DuplicateRecordsMap;
    loading: boolean;
    propertiesConfig: PropertyConfig[];
    existingDataMap: Map<string, FileRecord>;
    sheets: Sheet[];
    steps: WizardStepsMap;
}

type Props = WizardContextProps & FlashContextInterface & RouteComponentProps;

const importService = new ImportService();
class ImportWizard extends Component<Props, State> {
    static contextType = GuidedTourContext;

    constructor(props: Props) {
        super(props);
        const steps = Object.assign({}, WIZARD_STEPS_MAP);
        this.state = {
            currentStep: WIZARD_STEP_VALUES.SELECT_SHEET,
            duplicateRecordsMap: {},
            existingDataMap: new Map<string, FileRecord>(),
            loading: false,
            propertiesConfig: [],
            sheets: [],
            steps,
        };
    }

    async componentDidMount() {
        const { portfolioId, importId, type } = this.props.wizardContext;
        try {
            this.setLoading(true);
            const sheets = await importService.getSheets(portfolioId, importId);
            if (!sheets?.length) {
                return await this.cancel();
            } else if (sheets.length === 1) {
                await importService.postSheetName(portfolioId, importId, '', type);
            }
            let propertiesConfig: PropertyConfig[] = [];
            let existingDataMap: Map<string, FileRecord> = new Map<string, FileRecord>();
            if (!!RESOURCE_TYPES_MAP[type]) {
                propertiesConfig = await importService.getPropertiesConfigForType(portfolioId, type);
            }
            if (!RESOURCE_TYPES_MAP[type].allowDuplicates) {
                const existingDataItems: FileRecord[] = await importService.getPortfolioDataForType(portfolioId, type);
                existingDataMap = existingDataItems.reduce((map: Map<string, FileRecord>, item: FileRecord) => {
                    map.set(getKey(item, type), item);
                    return map;
                }, new Map<string, FileRecord>());
            }

            const steps = this.removeUnusedSteps(sheets);
            const currentStep = Object.keys(steps).length > 0 && steps[Object.keys(steps)[0]].value || WIZARD_STEP_VALUES.SELECT_SHEET;
            this.setState({
                currentStep,
                existingDataMap,
                propertiesConfig,
                sheets,
                steps,
            });
            this.setLoading(false);
        } catch (err) {
            this.setLoading(false);
            this.props.flash.alert(FlashType.error, 
                (err as ErrorWithResponse).response?.data?.message ?? (err as Error).message, constants.ERR_HEADERS.CANNOT_LOAD_DATASHEETS);
            this.cancel();
        }
    }

    render() {
        const { type } = this.props.wizardContext;
        const { currentStep, duplicateRecordsMap, existingDataMap, loading, propertiesConfig, sheets, steps } = this.state;
        return (
            <div className='import-wizard wizard-layout'>
                <div className='wizard-col-nav'>
                    <WizardNavbarView
                        currentStep={this.state.currentStep}
                        steps={this.state.steps}
                    />
                </div>
                <div className='wizard-col-content'>
                    <Box variant="h1">{`${constants.IMPORT_WIZARD_TITLE} ${RESOURCE_TYPES_MAP[type].label} - ${this.props.wizardContext.fileName}`} </Box>
                    <WizardContent
                        currentStep={currentStep}
                        setNextAndPrevHandlers={this.setNextAndPrevHandlers}
                        setLoading={this.setLoading}
                        sheets={sheets}
                        onDuplicatesChange={this.onDuplicatesChange}
                        cancel={this.cancel}
                        loading={loading}
                        duplicateRecordsMap={duplicateRecordsMap}
                        propertiesConfig={propertiesConfig}
                        existingDataMap={existingDataMap}
                    />
                    <WizardActionButtons
                        currentStep={currentStep}
                        steps={steps}
                        loading={loading}
                        cancel={this.cancel}
                        next={this.next}
                        prev={this.prev}
                    />
                </div>
            </div>
        );
    }

    removeUnusedSteps = (sheets: Sheet[]): WizardStepsMap => {
        const steps = this.state.steps;
        // Remove unused wizard steps: selectSheet and duplicateRecords
        if (sheets.length === 1) {
            delete steps[WIZARD_STEP_VALUES.SELECT_SHEET];
        }
        if (RESOURCE_TYPES_MAP[this.props.wizardContext.type].allowDuplicates) {
            delete steps[WIZARD_STEP_VALUES.DUPLICATE_RECORDS];
        }
        return steps;
    };

    getStepsNavigationOptions(): StepNagivationOptions {
        return {
            duplicateExist: (!RESOURCE_TYPES_MAP[this.props.wizardContext.type].allowDuplicates
            && !!Object.keys(this.state.duplicateRecordsMap).length),
        };
    }

    cancel = async (): Promise<void> => {
        const { portfolioId, importId, setStage } = this.props.wizardContext;
        try {
            await importService.clearStagingData(portfolioId, importId);
            setStage?.(STAGE_VALUES.DEFAULT);
        } catch (err) {
            this.props.flash.alert(FlashType.error, 
                (err as ErrorWithResponse).response?.data?.message ?? (err as Error).message, constants.ERR_HEADERS.CANNOT_CANCEL);
        }
    };

    next = async (): Promise<void> => {
        const { portfolioId, type } = this.props.wizardContext;
        this.setLoading(true);
        try {
            const onNext = this.state.steps[this.state.currentStep]?.onNext;
            if (onNext) {
                await onNext();
            }
            const nextStep = getNextStep(this.state.currentStep, this.getStepsNavigationOptions());
            if (nextStep) {
                this.setStep(nextStep);
            } else {
                this.setLoading(false);
                this.props.history.push(`/portfolio/${portfolioId}/management${RESOURCE_TYPES_MAP[type].url}`);
            }
        } catch (err) {
            if ((err as Error).message.includes('404')) {
                this.props.flash.alert(FlashType.error, 
                    (err as ErrorWithResponse).response?.data?.message ?? (err as Error).message, constants.ERR_HEADERS.CANNOT_FIND_STAGING_DATA);
            } else {
                this.props.flash.alert(FlashType.error, 
                    (err as ErrorWithResponse).response?.data?.message ?? (err as Error).message, constants.ERR_HEADERS.CANNOT_MOVE_NEXT);
            }
            this.setLoading(false);
        }
    };

    prev = async (): Promise<void> => {
        this.setLoading(true);
        try {
            const onPrev = this.state.steps[this.state.currentStep]?.onPrev;
            if (onPrev) {
                await onPrev();
            }
            const prevStep = getPrevStep(this.state.currentStep, this.getStepsNavigationOptions());
            if (prevStep) {
                this.setStep(prevStep);
            }
        } catch (err) {
            this.props.flash.alert(FlashType.error, 
                (err as ErrorWithResponse).response?.data?.message ?? (err as Error).message, constants.ERR_HEADERS.CANNOT_MOVE_PREVIOUS);
            this.setLoading(false);
        }
    };

    setNextAndPrevHandlers = (currentStep: WIZARD_STEP_VALUES, onNext: () => Promise<void>, onPrev: () => Promise<void>): void => {
        const steps = this.state.steps;
        if (steps[currentStep]) {
            steps[currentStep].onNext = onNext;
            steps[currentStep].onPrev = onPrev;
        }
        this.setState({
            steps,
        });
    };

    setLoading = (loading: boolean): void => {
        this.setState({
            loading,
        });
        this.context.setContentChanged();
    };

    setStep = (stepName: WIZARD_STEP_VALUES): void => {
        this.setState({
            currentStep: stepName,
        });
    };

    onDuplicatesChange = (duplicateRecordsMap: DuplicateRecordsMap): void => {
        this.setState({
            duplicateRecordsMap,
        });
    };
}

export default withRouter(withFlashContext(withWizardContext(ImportWizard)));
